import { ErrorBoundary } from "react-error-boundary";

// Material UI Components
import { Alert, Box, Collapse, Grid, Link, Typography } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import AutocompleteChipsLayout from "components/Autocomplete/AutocompleteChipsLayout";
import PeersChip from "../PeersChip";
import LocationsChip from "../LocationsChip";
import { Body } from "components/Layout/LayoutContent";
import GenericError from "components/Errors/GenericError";
import { CompanySummaryBody } from "components/CompanySummary";
import CustomHeader from "components/CustomHeader/CustomHeader";

// Hooks
import { useIntl } from "react-intl";
import useDomainNavActions from "./useDomainNavActions";
import useDomainLayout from "./useDomainLayout";

// Enums
import icons from "enums/icons";

// Styles
import domainLayoutStyles from "./DomainLayout.styles";
import OpenAdvancedSearchButton from "components/AdvancedPeersSearch/OpenAdvancedSearchButton";
import ProText from "components/Pro/ProText";

// Ability
import { AbilityContext } from "context/Ability";
import { useAbility } from "@casl/react";

const DomainLayout = (): JSX.Element => {
    const intl = useIntl();
    const classes = domainLayoutStyles();
    const ability = useAbility(AbilityContext);
    const canSeeAdvancedSearch = ability.can("see", "SAAS_LD_PROJECT_INSIGHT_DOMAIN_PEERS_SEARCH_ADV");

    const {
        sectors,
        products,
        company,
        peers,
        peersRequest,
        domain,
        country,
        companyCountryId,
        locations,
        autoCompleteProducts,
        listOfSectors,
        categorizedSectors,
        sectorsRequestStatus,
        showWarning,
        isLoaderVisible,
        setSectors,
        setProducts,
        onChangeLocation,
        onChangePeersChip,
        onEndTypingCallback,
        onChangeProductsOrSectors,
        handleInitialDataFetch,
    } = useDomainLayout();

    const { backButton, nextButton } = useDomainNavActions(
        peers,
        peersRequest,
        products,
        sectors,
        country,
        showWarning
    );

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <CustomHeader
                title="Set Domain"
                backButton={backButton}
                nextButton={nextButton}
                middlePlaceholder={<CompanySummaryBody companyData={company} />}
            />
            <Body>
                <Collapse
                    orientation="vertical"
                    in={showWarning}
                    easing={{
                        enter: "ease-out",
                        exit: "ease-in",
                    }}
                >
                    <Alert variant="filled" severity="warning" icon={false} className={classes.warningAlert}>
                        <Box display="flex" justifyContent="center">
                            <CustomIcon icon={icons.errorTriangle} className={classes.warningIcon} />
                            <Typography variant="subtitle1" className={classes.warningMessage}>
                                {intl.formatMessage({ id: "setup-company.domain.mainWarning" })}
                            </Typography>
                        </Box>
                    </Alert>
                </Collapse>
                <Grid className={classes.domainRoot} container>
                    <Grid item lg={6}>
                        <Box className={classes.domainSectionHeader}>
                            <Box className={classes.domainSectionTitle}>
                                <CustomIcon icon={icons.equalizer} className={classes.stepTitleIcon} />
                                <Typography variant="h4" className={classes.stepTitle}>
                                    {intl.formatMessage({ id: "setup-company.domain.refineDomain" })}
                                </Typography>
                            </Box>
                        </Box>
                        <AutocompleteChipsLayout
                            title={intl.formatMessage({
                                id: "setup-company.domain.sector",
                            })}
                            icon={<CustomIcon icon={icons.sector} className={classes.sectorIcon} />}
                            options={listOfSectors}
                            categorizedOptions={categorizedSectors}
                            values={sectors}
                            setValues={setSectors}
                            type="sectors"
                            asyncRequestStatus={sectorsRequestStatus}
                            rule={intl.formatMessage({ id: "setup-company.domain.sectors.rule" })}
                            showWarning={!isLoaderVisible && sectors.every((sector) => sector.selected === false)}
                            onChangeChips={onChangeProductsOrSectors}
                            onClickPlusButtonCallback={handleInitialDataFetch}
                        />
                        <AutocompleteChipsLayout
                            title={intl.formatMessage({
                                id: "setup-company.domain.products",
                            })}
                            icon={<CustomIcon icon={icons.product} className={classes.productIcon} />}
                            options={autoCompleteProducts.data}
                            values={products}
                            setValues={setProducts}
                            type="products"
                            asyncRequestStatus={autoCompleteProducts.status}
                            onEndTypingCallback={onEndTypingCallback}
                            rule={intl.formatMessage({ id: "setup-company.domain.products.rule" })}
                            showWarning={!isLoaderVisible && products.filter((product) => product.selected).length < 3}
                            onChangeChips={onChangeProductsOrSectors}
                            onClickPlusButtonCallback={handleInitialDataFetch}
                        />
                        <LocationsChip
                            locations={locations}
                            onChange={onChangeLocation}
                            disabled={!Boolean(companyCountryId)}
                        />

                        <PeersChip
                            peers={peers}
                            onChange={onChangePeersChip}
                            status={peersRequest}
                            erasable={true}
                            headerRightSide={
                                <Box className={classes.proContainer}>
                                    {!canSeeAdvancedSearch ? <ProText /> : null}
                                    <OpenAdvancedSearchButton
                                        sectors={sectors}
                                        products={products}
                                        peers={peers}
                                        company={company}
                                        isDisabled={peersRequest === "fetching"}
                                        countryId={country}
                                    />
                                </Box>
                            }
                        />
                    </Grid>
                    <Grid item lg={4}>
                        <Box className={classes.domainSectionTitle} mb={2}>
                            <CustomIcon icon={icons.paper2} className={classes.stepTitleIcon} />
                            <Typography variant="h4" className={classes.stepTitle}>
                                {intl.formatMessage({ id: "companySummary.about" })} {company?.name ?? ""}
                            </Typography>
                        </Box>
                        <Typography className={classes.companyDescription} variant="body1">
                            {company?.description ?? ""}
                        </Typography>
                        {domain && (
                            <Link href={`https://www.${domain}`} target="_blank" className={classes.visitWebSite}>
                                {intl.formatMessage({ id: "companySummary.website" })}
                                <CustomIcon icon={icons.circledChevronRight} />
                            </Link>
                        )}
                    </Grid>
                </Grid>
            </Body>
        </ErrorBoundary>
    );
};

export default DomainLayout;
